import { FC } from 'react';
import {
	alephToast,
	AngleDown,
	AngleUp,
	Button,
	ButtonAppearance,
	Dropdown,
	Menu,
	NotificationToast,
	NotificationType,
	translate,
} from '@aleph/shared-components';
import { useMutation } from '@apollo/client';
import UpdateClientDashboardSettingsDocument from 'wb-spotify/app/routes/superGrid/cellRenderers/name/api/updateClientDashboardSettings.graphql';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

export const GRID_DASHBOARD_BREAKDOWNS = [
	{
		value: 'platform',
		label: translate('sc: dashboard_breakdowns.platform', { defaultValue: 'Performance (KPI) / Platform' }),
	},
	{
		value: 'country',
		label: translate('sc: dashboard_breakdowns.country', { defaultValue: 'Performance (KPI) / Country' }),
	},
	{
		value: 'gender',
		label: translate('sc: dashboard_breakdowns.gender', { defaultValue: 'Performance (KPI) / Gender' }),
	},
	{
		value: 'age',
		label: translate('sc: dashboard_breakdowns.age', { defaultValue: 'Performance (KPI) / Age' }),
	},
	{
		value: 'genre',
		label: translate('sc: dashboard_breakdowns.genre', { defaultValue: 'Performance (KPI) / Genre' }),
	},
];

const SettingsBreakdownSelector: FC<ICellRendererParams> = (params) => {
	const { t } = useTranslation();
	const [updateKpiDocument] = useMutation(UpdateClientDashboardSettingsDocument);

	const unsetKPI = () => {
		params?.node?.setData({
			...params?.data,
			clientKpi:       null,
			clientBreakdown: null,
		});
	};

	const setBreakdown = async (newBreakdown: string, onClose: () => void) => {
		const { errors } = await updateKpiDocument({
			variables: {
				clientId:        params?.data?.clientId,
				clientBreakdown: newBreakdown,
			},
		});

		if (errors) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={
						t(
							're: something went wrong while updating the kpi',
							'Something went wrong while updating the KPI',
						)
					}
				/>,
			);

			onClose();

			return;
		}

		alephToast(
			<NotificationToast
				type={NotificationType.Success}
				title={t('re: kpi updated', 'KPI updated')}
			/>,
		);

		params?.node?.setData({
			...params?.data,
			clientBreakdown: newBreakdown,
		});

		onClose();
	};

	return (
		<div className="h-full flex items-center justify-center">
			<Dropdown
				usePortal
				triggerControl={
					({ isOpen, closeDropdown, openDropdown }) => (
						<span>
							<Button
								active={isOpen}
								leftNode={
									isOpen
										? (
											<AngleUp
												width={16}
												height={16}
											/>)
										: (
											<AngleDown
												width={16}
												height={16}
											/>
										)
								}
								className="hover:!ring-transparent"
								appearance={ButtonAppearance.GHOST}
								onClick={isOpen ? closeDropdown : openDropdown}
							/>
						</span>
					)
				}
			>
				{(closeDropdown) => (
					<Menu>
						<>
							<Menu.Item
								className="text-gray-500"
								disabled
							>
								CUSTOMIZE
							</Menu.Item>
							<Menu.Item onClick={unsetKPI}>
								{t('sc: kpi.edit', 'Edit main KPI')}
							</Menu.Item>
							<Menu.Item
								className="text-gray-500"
								disabled
							>
								CHARTS
							</Menu.Item>
							{GRID_DASHBOARD_BREAKDOWNS.map((breakdown) => (
								<Menu.Item
									key={breakdown.value}
									onClick={() => {
										setBreakdown(breakdown.value, closeDropdown);
									}}
									selected={params?.data?.clientBreakdown === breakdown.value}
								>
									{breakdown.label}
								</Menu.Item>
							))}
						</>
					</Menu>
				)}
			</Dropdown>
		</div>
	);
};

export default SettingsBreakdownSelector;
